import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { persistor } from "../redux/store";
import { addTab, setActiveTab, closeTab } from "../redux/action/tab.action";
import Sidebar from "../components/Sidebar";
import TabWrapper from "./TabWrapper";
import DialogBox from "../components/DialogBox";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import { isTokenValid, userLogout, setDefaultFacility } from "../services/auth/auth";
import TempUserDetails from "./tempUser/TempUserDetails";
import { toast } from "../components/AppToast";
import { getMenuBasedOnRole } from "../services/global/global";
import ErrorBoundary from "../components/ErrorBoundary";
import { login } from "../redux/action/login.action";
import { setPermissions } from "../redux/action/permissions.action";
import { ensureManagementMenus, findDashboardMenuLeaf, normalizeBootstrapIconClass, removeInventoryDetailMenus, renameMenuTitles, sortMenusWithDataSetupLast } from "../utils/sidebarUtils";
import { applyThemePreferences, resolveActiveBranding } from "../utils/branding";
import { clearItemLocationFocus, showInventorySection } from "./sku-management/item-location/itemLocationFocus";



const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userDetail, tokens } = useSelector((state) => state.login);
  const { tabs, activeTab } = useSelector((state) => state.tab);

  const fetchedMenusRef = useRef(false);

  // Token states
  const [tokenVerified, setTokenVerified] = useState(false);
  const [tokenChecking, setTokenChecking] = useState(true); // Block everything until token is checked

  // UI states
  const { navData } = useSelector((state) => state.permissions);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showTabLimitPopup, setShowTabLimitPopup] = useState(false);
  const [defaultFacility, setDefaultFacilityState] = useState(null);

  const userRole = userDetail?.userType;
  const defaultFacilityID = userDetail?.facilityId;
  const FacilityLists = userDetail?.roleFacilityAccess || [];

  // -------------------------
  // Verify token on mount
  // -------------------------
  // Keep the branding captured at login if a later response omits it.
  const withBranding = (nextUser) => ({
    ...(nextUser || {}),
    branding: nextUser?.branding ?? userDetail?.branding ?? null,
  });

  const verifyToken = async () => {
    try {
      const updateData = await isTokenValid(tokens?.accessToken);
      dispatch(login(withBranding(updateData)));
      setTokenVerified(true);
    } catch (err) {
      await userLogout(tokens?.accessToken);
      localStorage.clear();
      dispatch({ type: "RESET_STORE" });
      persistor.purge();
      navigate("/login");
    } finally {
      setTokenChecking(false);
    }
  };

  useEffect(() => {
    if (tokens?.accessToken) {
      verifyToken();
    } else {
      // No token, logout immediately
      navigate("/login");
    }
  }, [tokens]);

  // Apply theme from the logged-in user's branding:
  // partnerBranding -> tenantBranding -> defaults.
  useEffect(() => {
    if (!userDetail) return;
    applyThemePreferences(resolveActiveBranding(userDetail).preferences);
  }, [userDetail]);

  // -------------------------
  // Fetch menus after token verified
  // -------------------------
const fetchMenus = async () => {
  try {
    const data = await getMenuBasedOnRole();

    const updatedMenu = renameMenuTitles(
      sortMenusWithDataSetupLast(
        removeInventoryDetailMenus(
          ensureManagementMenus(
            data.map((el) => ({
              ...el,
              collapsed: false,
            }))
          )
        )
      )
    );

      console.log(updatedMenu);
      
    // 🔥 Store menus in Redux
    dispatch(setPermissions(updatedMenu));

  } catch (err) {
    toast.error(`Failed to fetch menus: ${err}`);
  }
};


  useEffect(() => {
    if (tokenVerified && userDetail && !fetchedMenusRef.current) {
      if (!["TENANT_USER", "PARTNER_USER"].includes(userRole) || defaultFacilityID) {
        fetchMenus();
        fetchedMenusRef.current = true;
      }
    }
  }, [userDetail, tokenVerified, defaultFacilityID]);

  // Open Dashboard tab when landing with no tabs (e.g. after login) instead of the welcome placeholder
  useEffect(() => {
    if (!tokenVerified || !navData?.length || tabs.length > 0) return;
    const fromMenu = findDashboardMenuLeaf(navData);
    const tab = fromMenu || {
      title: "Dashboard",
      path: "/dashboard",
      content: "Dashboard",
    };
    dispatch(addTab(tab));
  }, [tokenVerified, navData, tabs.length, dispatch]);

  // -------------------------
  // Set Default Facility
  // -------------------------
  const handleSetDefaultFacility = async () => {
    if (!defaultFacility) {
      toast.error("Please select a facility");
      return;
    }
    try {
      const selected = FacilityLists.find((item) => item.id == defaultFacility);
      const updateData = await setDefaultFacility(selected);
      dispatch(login(withBranding(updateData)));
      toast.success("Default facility set successfully");
      // fetchMenus();
    } catch (err) {
      console.log(err);

      toast.error("Failed to set default facility");
    }
  };

  // -------------------------
  // Sidebar responsiveness
  // -------------------------
  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth > 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // -------------------------
  // Logout
  // -------------------------
  const logout = async () => {
    await userLogout(tokens?.accessToken);
    localStorage.clear();
    dispatch({ type: "RESET_STORE" });
    persistor.purge();
    navigate("/login");
  };

  // -------------------------
  // Tabs
  // -------------------------
  const navClickHandler = (title, path, content, iconRaw) => {
    if (content === "Inventory" || String(title || "").trim().toLowerCase() === "inventory visibility") {
      clearItemLocationFocus();
      showInventorySection("inventory");
    }
    const existingIndex = tabs.findIndex((tab) => tab.path === path);
    const maxTabs = tabs.some((tab) => tab.path === "/settings") ? 11 : 10;

    if (existingIndex !== -1) dispatch(setActiveTab(existingIndex));
    else if (tabs.length >= maxTabs) setShowTabLimitPopup(true);
    else {
      const iconClass = normalizeBootstrapIconClass(iconRaw);
      dispatch(addTab({ title, path, content, ...(iconClass ? { iconClass } : {}) }));
    }
  };

  const handleCloseModal = () => setShowTabLimitPopup(false);
  const modalContent = () => <p>You can open a maximum of 10 tabs at a time. Please close an existing tab to open a new one.</p>;
  const modalFooterContent = () => <button className="btn btn-secondary" onClick={handleCloseModal}>OK</button>;

  // -------------------------
  // BLOCK RENDERING UNTIL TOKEN VERIFIED
  // -------------------------
  // if (tokenChecking) {
  //   return (
  //     // <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
  //     //   <p>Verifying session...</p>
  //     // </div>
  //   );
  // }

  // -------------------------
  // DEFAULT FACILITY SELECTION
  // -------------------------
  if ((userRole === "TENANT_USER" || userRole === "PARTNER_USER") && !defaultFacilityID) {
    
    const selectedFacility = FacilityLists.find((fac) => fac.id == defaultFacility);
    const selectedRoleName = selectedFacility?.roleName || "";

    return (
      <div className="main_container">
        <TopHeader onLogout={logout} />
        <div className="select-facility-container d-flex justify-content-center align-items-center vh-100 bg-light">
          <div className="facility-card bg-white p-4 rounded shadow">
            <h2 className="text-center mb-4">Select Default Facility</h2>

            <div className="mb-3 text-start">
              <label className="form-label fw-medium">Facility</label>
              <select
                className="form-select"
                value={defaultFacility || ""}
                onChange={(e) => setDefaultFacilityState(e.target.value)}
              >
                <option value="">-- Select Facility --</option>
                {FacilityLists.map((fac) => (
                  <option key={fac.id} value={fac.id}>
                    {fac.facilityName}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4 text-start">
              <label className="form-label fw-medium">Role Name</label>
              <input
                type="text"
                value={selectedRoleName}
                readOnly
                className="form-control bg-body-secondary"
              />
            </div>

            <button
              className="btn btn-primary w-100 py-2"
              onClick={handleSetDefaultFacility}
            >
              Set Default Facility
            </button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // -------------------------
  // NORMAL HOME UI
  // -------------------------
  return (
    <ErrorBoundary>
      <div className="main_container">
        <TopHeader onLogout={logout} />
        {userRole !== "TEMP_USER" ? (
          <div className="content">
            {isSidebarOpen && <Sidebar navData={navData} isOpen={isSidebarOpen} onNavClick={navClickHandler} />}
            <ErrorBoundary>
              <TabWrapper tabs={tabs} activeTab={activeTab} setActiveTab={(idx) => dispatch(setActiveTab(idx))} closeTab={(idx) => dispatch(closeTab(idx))} />
            </ErrorBoundary>
          </div>
        ) : (
          <div className="content-wrapper">
            <ErrorBoundary>
              <TempUserDetails />
            </ErrorBoundary>
          </div>
        )}
        <Footer />
        {showTabLimitPopup && <DialogBox modalTitle="Tab Limit Reached" modalFooter={modalFooterContent()} modalContent={modalContent()} onClose={handleCloseModal} className="tab-modal" />}
      </div>
    </ErrorBoundary>
  );
};

export default Home;
